import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  LoadingDialogComponent,
} from "@cotecna/dialogs";
import { ActionElement } from "@cotecna/material";
import { firstValueFrom, Subscription } from "rxjs";
import { User } from "src/app/session/models/user";
import { StorageService } from "src/app/session/services/storage.service";
import { UserService } from "src/app/session/services/user.service";
import { AccountSettingsAvailableTags } from "src/app/settings/models/available-form";
import { AccountTagService } from "src/app/settings/services/account-tags.service";
import { FeatureFlagsService } from "src/app/shared/services/feature-flags/feature-flags.service";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import {
  COTECNA_USER_KEY,
  DELETE_SINGLE_PICTURE_MESSAGE,
  DOWNLOAD_ALL_PICTURES_MESSAGE,
  DOWNLOAD_PICTURES_MESSAGE,
  DOWNLOAD_SINGLE_PICTURE_MESSAGE,
  FAB_ACTION_GET_ALL_PICTURES,
  FAB_ACTION_GET_FILTERED_PICTURES,
  FAB_ACTION_GET_TAB_PICTURES,
  SHARE_ALL_PICTURES_MESSAGE,
  SHARE_SINGLE_PICTURE_MESSAGE,
  UNSHARE_ALL_PICTURES_MESSAGE,
  UNSHARE_SINGLE_PICTURE_MESSAGE,
} from "../../constants/constants";
import { AddPhotosComponent } from "../../dialogs/add-photos/add-photos.component";
import { AddPhotosDialogData } from "../../dialogs/add-photos/add-photos-dialog-data";
import {
  GeneratePicturesReportDialogComponent,
  GeneratePicturesReportDialogData,
  GeneratePicturesReportDialogResult,
} from "../../dialogs/generate-pictures-report-dialog/generate-pictures-report-dialog.component";
import {
  PicturesReportConfigDialogComponent,
  PicturesReportConfigDialogData,
} from "../../dialogs/pictures-report-config-dialog/pictures-report-config-dialog.component";
import { ViewPhotosDialogComponent } from "../../dialogs/view-photos-dialog/view-photos-dialog.component";
import { InspectionCommonData } from "../../model/inspection-common-data";
import { Photo } from "../../model/photo";
import { InspectionDetailsService } from "../../services/inspection-details.service";
import { PhotoCacheService } from "../../services/photo-cache.service";
import { InspectionDetailsBase } from "../inspection-details-base";
import { PictureThumbnail } from "./inspection-details-photo.interface";

interface ToggleState {
  isActive: boolean;
}

@Component({
  selector: "im-inspection-details-photos",
  templateUrl: "./inspection-details-photos.component.html",
  styleUrls: ["./inspection-details-photos.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InspectionDetailsPhotosComponent
  extends InspectionDetailsBase
  implements OnInit, OnDestroy
{
  public speedDialActions: ActionElement[] = [
    {
      icon: "image",
      name: FAB_ACTION_GET_TAB_PICTURES,
      text: FAB_ACTION_GET_TAB_PICTURES,
      tooltip: FAB_ACTION_GET_TAB_PICTURES,
    },
    {
      icon: "photo_library",
      name: FAB_ACTION_GET_ALL_PICTURES,
      text: FAB_ACTION_GET_ALL_PICTURES,
      tooltip: FAB_ACTION_GET_ALL_PICTURES,
    },
  ];

  public speedDialOptionalActions: ActionElement[] = [
    {
      icon: "filter_alt",
      name: FAB_ACTION_GET_FILTERED_PICTURES,
      text: FAB_ACTION_GET_FILTERED_PICTURES,
      tooltip: FAB_ACTION_GET_FILTERED_PICTURES,
    },
  ];

  public uploadButtonBottomMargin: number = 16;
  public isToggleActive: boolean = false;
  public photos: Photo[] = [];
  public isLoading: boolean = true;
  public allPhotosShared: boolean = false;
  public creationDateFrom: Date | null = null;
  public creationDateTo: Date | null = null;

  public selectedTags = new FormControl([] as AccountSettingsAvailableTags[]);
  public tagsList: AccountSettingsAvailableTags[] = [];

  private selectedTagIds: string[];
  private inspectionName: string;
  private inspectionCommonDataSubscription: Subscription;
  private user: User;
  @Input()
  declare public inspectionId: string;

  public constructor(
    route: ActivatedRoute,
    private inspectionDetailsService: InspectionDetailsService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private featureFlagsService: FeatureFlagsService,
    private storageService: StorageService,
    private userService: UserService,
    private userRolePermissionsService: UserRolePermissionsService,
    private readonly accountTagService: AccountTagService,
    private readonly photoCacheService: PhotoCacheService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super(route);
  }

  public async ngOnInit() {
    this.updateUploadButtonStyle();
    window.addEventListener("resize", this.updateUploadButtonStyle.bind(this));
    this.subscribeToInspectionCommonData();
    await this.getTagList();
    this.user = await this.storageService.get(COTECNA_USER_KEY);
    await this.getInspectionPictures();
    this.selectedTags.valueChanges.subscribe(
      (selectedTags: AccountSettingsAvailableTags[] | null) => {
        const normalizedTags = selectedTags ?? [];
        const tagIds = normalizedTags.map((tag) => tag.id);
        this.selectedTagIds = tagIds;
        const hasFilter = this.speedDialActions.some(
          (a) => a.name === FAB_ACTION_GET_FILTERED_PICTURES,
        );
        const nextActions = [...this.speedDialActions];
        if (normalizedTags.length && !hasFilter) {
          nextActions.unshift(this.speedDialOptionalActions[0]);
        } else if (!normalizedTags.length && hasFilter) {
          const filterIndex = nextActions.findIndex(
            (a) => a.name === FAB_ACTION_GET_FILTERED_PICTURES,
          );
          if (filterIndex >= 0) {
            nextActions.splice(filterIndex, 1);
          }
        }
        this.speedDialActions = nextActions;
        this.filterPictures(tagIds);
      },
    );
  }

  public async ngOnDestroy() {
    this.inspectionCommonDataSubscription?.unsubscribe();
    await this.photoCacheService.clearCache();
  }

  public updateUploadButtonStyle() {
    const bottomBarSize: number = 48;
    const buttonSize: number = 56;
    const buttonMargin: number = 16;

    const extraSpace = this.canDownloadOrDeletePictures()
      ? buttonSize + buttonMargin
      : 0;
    if (window.innerWidth < 959) {
      this.uploadButtonBottomMargin = buttonMargin + bottomBarSize + extraSpace;
    } else {
      this.uploadButtonBottomMargin = buttonMargin + extraSpace;
    }
  }

  public getUploadButtonStyle() {
    return `bottom: ${this.uploadButtonBottomMargin}px !important`;
  }

  public canDownloadOrDeletePictures(): boolean {
    return this.userRolePermissionsService.canDownloadOrDeletePictures();
  }

  public handleKeyDown(event: KeyboardEvent, filename: string) {
    if (event.key === "Enter" || event.key === " ") {
      this.openViewPhotoDialog(filename);
    }
  }

  public canUploadPictures(): boolean {
    return this.userRolePermissionsService.canUploadPictures();
  }

  public onFabSpeedDialSelectedAction(actionElement: ActionElement): void {
    const actionName = actionElement.name;
    switch (actionName) {
      case FAB_ACTION_GET_TAB_PICTURES:
        this.downloadPictures();
        break;
      case FAB_ACTION_GET_ALL_PICTURES:
        this.downloadAllPictures();
        break;
      case FAB_ACTION_GET_FILTERED_PICTURES:
        this.downloadFilteredPictures();
        break;
    }
  }

  public onFabSpeedDialStateChanged(toggleState: ToggleState): void {
    this.isToggleActive = toggleState.isActive;
  }

  public onShareAllPhotos(toggleEvent: any): void {
    (() =>
      toggleEvent.checked
        ? this.shareAllPhotos(toggleEvent)
        : this.unshareAllPhotos(toggleEvent))();
  }

  private async shareAllPhotos(toggleEvent: any): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Share images with Customer Portal",
        message: `All images on this inspection are going to be shared with client at Customer Portal.\n\nAll new images to be added will be also shared by default.\n\nDo you want to confirm?`,
      },
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        SHARE_ALL_PICTURES_MESSAGE,
      );
      try {
        await this.inspectionDetailsService.shareAllPictures(this.inspectionId);
        this.allPhotosShared = true;
        this.photos = this.photos.map((photo) => ({
          ...photo,
          shared: true,
        }));
      } finally {
        loadingDialogRef.close();
        this.snackBar.open(
          "All images has been shared with external source",
          "CLOSE",
          { duration: 3000, panelClass: ["success-snackbar"] },
        );
      }
    } else {
      toggleEvent.source.checked = false;
    }
  }

  public removeTag(tag) {
    const currentTags = this.selectedTags.value ?? [];
    const index = currentTags.findIndex(
      (selectedTag) => selectedTag.id === tag,
    );
    if (index >= 0) {
      const nextTags = [...currentTags];
      nextTags.splice(index, 1);
      this.selectedTags.setValue(nextTags);
    }
  }

  public async onStartDateChange(event: any) {
    this.creationDateFrom = event.value;
    if (
      this.creationDateFrom != null &&
      this.creationDateTo != null &&
      new Date(this.creationDateFrom) <= new Date(this.creationDateTo)
    ) {
      this.filterPictures(this.selectedTagIds);
      const actionToAdd = this.speedDialOptionalActions[0];
      const exists = this.speedDialActions.some(
        (action) => action.name === actionToAdd.name,
      );

      if (!exists) {
        this.speedDialActions = [actionToAdd, ...this.speedDialActions];
      }
    }
  }

  public async onEndDateChange(event: any) {
    this.creationDateTo = event.value;
    if (
      this.creationDateFrom != null &&
      this.creationDateTo != null &&
      new Date(this.creationDateFrom) <= new Date(this.creationDateTo)
    ) {
      this.filterPictures(this.selectedTagIds);
      const actionToAdd = this.speedDialOptionalActions[0];
      const exists = this.speedDialActions.some(
        (action) => action.name === actionToAdd.name,
      );

      if (!exists) {
        this.speedDialActions = [actionToAdd, ...this.speedDialActions];
      }
    }
  }

  public async generatePicturesReport(): Promise<void> {
    const firstDialogRef = this.dialog.open(
      GeneratePicturesReportDialogComponent,
      {
        data: <GeneratePicturesReportDialogData>{
          templates: [],
          inspectionId: this.inspectionId,
        },
        autoFocus: false,
        disableClose: true,
        width: "600px",
        maxWidth: "90vw",
      },
    );
    const firstResult: GeneratePicturesReportDialogResult =
      await firstValueFrom(firstDialogRef.afterClosed());
    if (!firstResult) return;

    this.dialog.open(PicturesReportConfigDialogComponent, {
      data: <PicturesReportConfigDialogData>{
        availableTags: this.tagsList,
        defaultReportName: firstResult.template?.name ?? "",
        templateId: firstResult.templateId ?? null,
        inspectionId: this.inspectionId,
      },
      autoFocus: false,
      disableClose: true,
      height: "75vh",
      width: "80vw",
      maxWidth: "90vw",
    });
  }

  public clearDates() {
    this.creationDateFrom = null;
    this.creationDateTo = null;
    if (this.selectedTagIds?.length > 0) {
      this.filterPictures(this.selectedTagIds);
    } else {
      this.getInspectionPictures();
    }
  }

  private async unshareAllPhotos(toggleEvent: any): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Stop sharing images on external sources",
        message: `Stop sharing all images on this inspection with client at Customer Portal.\n\nDo you want to confirm?`,
      },
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        UNSHARE_ALL_PICTURES_MESSAGE,
      );
      try {
        await this.inspectionDetailsService.unshareAllPictures(
          this.inspectionId,
        );
        this.allPhotosShared = false;
        this.photos = this.photos.map((photo) => ({
          ...photo,
          shared: false,
        }));
      } finally {
        loadingDialogRef.close();
        this.snackBar.open(
          "All images has been unshared with external source",
          "CLOSE",
          { duration: 3000, panelClass: ["success-snackbar"] },
        );
      }
    } else {
      toggleEvent.source.checked = true;
    }
  }

  public onSharePhoto(toggleEvent: any, photo: Photo): void {
    (() =>
      toggleEvent.checked
        ? this.sharePhoto(toggleEvent, photo)
        : this.unsharePhoto(toggleEvent, photo))();
  }

  private async sharePhoto(toggleEvent: any, photo: Photo): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Share images on external sources",
        message: `Confirm to share the image on external sources with client`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        SHARE_SINGLE_PICTURE_MESSAGE,
      );
      try {
        await this.inspectionDetailsService.sharePicture(
          this.inspectionId,
          photo.filename,
        );
        photo.shared = true;
      } finally {
        loadingDialogRef.close();
      }
    } else {
      toggleEvent.source.checked = false;
    }
  }

  private async unsharePhoto(toggleEvent: any, photo: Photo): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Stop sharing images on external sources",
        message: `Confirm to stop sharing the image on external sources with client`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        UNSHARE_SINGLE_PICTURE_MESSAGE,
      );
      try {
        await this.inspectionDetailsService.unsharePicture(
          this.inspectionId,
          photo.filename,
        );
        photo.shared = false;
      } finally {
        loadingDialogRef.close();
      }
    } else {
      toggleEvent.source.checked = true;
    }
  }

  public async downloadImage(photo: Photo): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "get_app",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Download image",
        message: `Do you want to download the picture?`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        DOWNLOAD_SINGLE_PICTURE_MESSAGE,
      );
      try {
        await this.inspectionDetailsService.downloadPicture(
          this.inspectionId,
          this.inspectionName,
          photo,
        );
      } finally {
        loadingDialogRef.close();
      }
    }
  }

  public async deleteImage(photo: Photo): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "delete",
        okButton: {
          text: "delete",
          color: "warn",
        },
        title: "Delete image",
        message: `Do you want to delete the picture?`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        DELETE_SINGLE_PICTURE_MESSAGE,
      );
      try {
        await this.inspectionDetailsService.deletePicture(
          this.inspectionId,
          photo,
        );
        this.photos = this.photos.filter(
          (pic) => pic.filename != photo.filename,
        );
      } finally {
        loadingDialogRef.close();
      }
    }
  }

  public allowSharePictures(): boolean {
    if (
      this.featureFlagsService.allowSharePictures() &&
      this.photos.length > 0
    ) {
      const isUserAllowed = this.userService.shareAllowed(this.user);
      if (isUserAllowed) {
        return true;
      }
    }
    return false;
  }

  public allowALLSharePictures(): boolean {
    if (this.userService.shareAllowed(this.user) && this.photos.length > 0) {
      return true;
    }
    return false;
  }

  private subscribeToInspectionCommonData() {
    this.inspectionCommonDataSubscription =
      this.inspectionDetailsService.inspectionCommonData.subscribe(
        (inspectionCommonData: InspectionCommonData) => {
          this.inspectionName = inspectionCommonData?.name;
        },
      );
  }

  private async getTagList() {
    this.tagsList = (await this.accountTagService.getAvailableTags?.()) ?? [];
  }

  private async getInspectionPictures() {
    this.photos = [];
    try {
      const inspectionPictures: PictureThumbnail[] =
        await this.inspectionDetailsService.getPictures(
          this.inspectionId,
          this.creationDateFrom,
          this.creationDateTo,
        );
      inspectionPictures.map((picture: PictureThumbnail) =>
        this.showPhotoOnGallery(
          picture.thumbnail,
          picture.fileName,
          picture.shared,
        ),
      );

      if (this.featureFlagsService.allowSharePictures()) {
        this.allPhotosShared =
          await this.inspectionDetailsService.getSharePicturesStatus(
            this.inspectionId,
          );
      }
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private async filterPictures(tagIds: string[]) {
    const filteredPictures: PictureThumbnail[] =
      await this.inspectionDetailsService.getFilteredPictures(
        this.inspectionId,
        tagIds,
        this.creationDateFrom,
        this.creationDateTo,
      );
    this.photos = [];
    for (const picture of filteredPictures) {
      this.showPhotoOnGallery(
        picture.thumbnail,
        picture.fileName,
        picture.shared,
      );
    }
    this.changeDetectorRef.detectChanges();
  }

  private showPhotoOnGallery(photo: string, filename: string, shared: boolean) {
    const myPhoto: string = "data:image/jpeg;base64," + photo;
    this.photos.push({
      photo: this.sanitizer.bypassSecurityTrustResourceUrl(myPhoto),
      shared: shared,
      filename,
    });
  }

  public noPicturesAfterLoading() {
    return !this.isLoading && this.photos.length === 0;
  }

  private async downloadPictures() {
    const loadingDialogRef = this.openLoadingDialog(DOWNLOAD_PICTURES_MESSAGE);
    try {
      await this.inspectionDetailsService.downloadPictures(
        this.inspectionId,
        this.inspectionName,
      );
    } finally {
      loadingDialogRef.close();
    }
  }

  private async downloadAllPictures() {
    const loadingDialogRef = this.openLoadingDialog(
      DOWNLOAD_ALL_PICTURES_MESSAGE,
    );
    try {
      await this.inspectionDetailsService.downloadAllPictures(
        this.inspectionId,
        this.inspectionName,
      );
    } finally {
      loadingDialogRef.close();
    }
  }

  private async downloadFilteredPictures() {
    const loadingDialogRef = this.openLoadingDialog(DOWNLOAD_PICTURES_MESSAGE);
    try {
      await this.inspectionDetailsService.downloadFilteredPictures(
        this.inspectionId,
        this.inspectionName,
        this.selectedTags.value.map((tag) => tag.id),
        this.creationDateFrom,
        this.creationDateTo,
      );
    } finally {
      loadingDialogRef.close();
    }
  }

  public async uploadPictures(): Promise<void> {
    const dialogRef = this.dialog.open(AddPhotosComponent, {
      data: {
        inspectionId: this.inspectionId,
      } as AddPhotosDialogData,
      panelClass: "dialog",
    });
    const dialogResult = await dialogRef?.afterClosed().toPromise();
    if (dialogResult) {
      this.photos = [];
      this.isLoading = true;
      await this.getInspectionPictures();
      this.changeDetectorRef.detectChanges();
    }
  }

  private openLoadingDialog(message: string) {
    return this.dialog.open(LoadingDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: message,
    });
  }

  public showDownloadButton(): boolean {
    return !this.isLoading && this.canDownloadOrDeletePictures();
  }

  public showUploadButton(): boolean {
    return !this.isLoading && !this.isToggleActive && this.canUploadPictures();
  }

  public async openViewPhotoDialog(photoName: string): Promise<void> {
    const subscription = this.photoCacheService.photoDeleted$.subscribe(
      (photoName) => {
        this.photos = this.photos.filter(
          (photo) => photo.filename !== photoName,
        );
      },
    );

    let loadingDialog: MatDialogRef<any> = null;
    let viewPhotoDialog: MatDialogRef<any> = null;
    try {
      loadingDialog = this.dialog.open(LoadingDialogComponent, {
        autoFocus: false,
        disableClose: true,
        data: "Loading options",
      });
      loadingDialog?.close();
      viewPhotoDialog = this.dialog.open(ViewPhotosDialogComponent, {
        data: {
          inspectionId: this.inspectionId,
          selectedPhoto: photoName,
          photos: this.photos,
          inspectionName: this.inspectionName,
        },
        width: "70vw",
        height: "90vh",
        disableClose: true,
      });
      const result = await firstValueFrom(viewPhotoDialog?.afterClosed());
      if (result?.action === "refresh" && result?.photoName) {
        this.photos = this.photos.filter(
          (photo) => photo.filename !== result.photoName,
        );
      }
    } finally {
      loadingDialog?.close();
      subscription.unsubscribe();
    }
  }

  public canGeneratePhotoReport(): boolean {
    return (
      this.userRolePermissionsService.hasInspectorRole() ||
      this.userRolePermissionsService.isCoordinatorOrServiceProviderCoordinator()
    );
  }
}
